// src/components/HomeUser.js
import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Grid } from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

import Header from './Header';
import api from './api'; // Axios preconfigurado
import {
  StyledMainContainer,
  StyledHeaderWrapper,
  StyledPageTitle
} from './StyleComponents';

const COLORS = ['#009cde', '#002554'];

const BAR_COLORS = {
  'Verde': '#4CAF50',
  'Amarillo': '#FFC107',
  'Rojo': '#F44336',
};

function HomeUser() {
  const [rutinas, setRutinas] = useState([]);
  const [userRole, setUserRole] = useState('');
  const [userPlantas, setUserPlantas] = useState([]);

  useEffect(() => {
    const fetchUserAndRutinas = async () => {
      try {
        const userRes = await api.get('/users/me');
        const user = userRes.data;
        setUserRole(user.role);
        setUserPlantas(user.plantas);

        const rutinasRes = await api.get('/rutinas/?skip=0&limit=1');
        const rutinasData = rutinasRes.data;

        const rutinasAsignadas = rutinasData.filter(r =>
          user.role === 'admin' || (
            r.tipo_rutina?.ejecutor === user.role &&
            user.plantas.includes(r.planta_id)
          )
        );

        setRutinas(rutinasAsignadas);
      } catch (err) {
        console.error('Error al obtener rutinas o usuario:', err);
      }
    };

    fetchUserAndRutinas();
  }, []);

  const respondidas = rutinas.filter(r => r.status === 'respondida');
  const pendientes = rutinas.filter(r => r.status === 'pendiente');

  const rutinaData = [
    { name: 'Respondidas', value: respondidas.length },
    { name: 'Pendientes', value: pendientes.length },
  ];

  const cumplimientoData = [
    { name: 'Verde', count: respondidas.filter(r => r.semaforo === 'verde').length },
    { name: 'Amarillo', count: respondidas.filter(r => r.semaforo === 'amarillo').length },
    { name: 'Rojo', count: respondidas.filter(r => r.semaforo === 'rojo').length },
  ];

  return (
    <Box sx={{ backgroundColor: '#f9f9f9' }}>
      <Header />
      <StyledHeaderWrapper>
        <StyledPageTitle variant="h4" component="h1" gutterBottom>
          Home
        </StyledPageTitle>
      </StyledHeaderWrapper>

      <Grid container spacing={4}>
        {/* Rutinas asignadas */}
        <Grid item xs={12} sm={6}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: '8px' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Rutinas asignadas para el día de hoy
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
              <ResponsiveContainer width="50%" height={200} minWidth={250}>
                <PieChart>
                  <Pie
                    data={rutinaData}
                    cx="50%"
                    cy="50%"
                    innerRadius={0}
                    outerRadius={100}
                    paddingAngle={1}
                    dataKey="value"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {
                      rutinaData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))
                    }
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="h7">
                  Respondidas: {respondidas.length}
                </Typography>
                <Typography variant="h7">
                  Pendientes: {pendientes.length}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Cumplimiento de plantas asignadas */}
        <Grid item xs={12} sm={6}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: '8px' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Cumplimiento de plantas asignadas
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={cumplimientoData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count">
                  {
                    cumplimientoData.map((entry, index) => (
                      <Cell key={`bar-cell-${index}`} fill={BAR_COLORS[entry.name]} />
                    ))
                  }
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default HomeUser;
